import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AnimatePresence,
  cubicBezier,
  motion,
  useReducedMotion,
} from 'framer-motion';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { NavigationButtons } from '@features/onboarding/components/shared/ui/NavigationButtons';
import { BottomNavigationButtons } from '@features/onboarding/components/shared/ui/BottomNavigationButtons';
import { RightArrowIcon } from '@components/shared/icon/RightArrowIcon';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useUnderReviewStore } from 'src/store/under-review';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useAIStore } from 'src/store/ai';
import { useCompareStore } from 'src/store/compare';

import { cn } from '@utils/index';
import { getUpdateErrorMessage } from '@utils/errorHandling';
import { UpdateErrorModal } from '@features/onboarding/components/shared/modal/update-error/UpdateErrorModal';
import {
  // FormStepID,
  FormStepSections,
} from '@features/onboarding/enums/formSections.enum';
import { CONDITION_DESCRIPTION_VALIDATION_ERROR } from '@features/onboarding/components/6-health-question-form/constants';
// import { StepNavigator } from '@features/stepNavigator/stepNavigator';
import { ACCEPTANCE_WARNING_LS_KEY } from 'src/constants';
import { useW2Mode, useIsW2Mode } from '@features/w2/store/w2-mode';
import { W2_PHQ_STEP_LABELS } from '@features/w2/copy';

const StepBlockContext = React.createContext(false);
export const useStepBlockContext = () => React.useContext(StepBlockContext);

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const formatText = (text: string): string => {
  if (!text) return '';
  let formatted = text.replace(/\n/g, '<br />');
  formatted = formatted.replace(/([.!?])([A-ZА-ЯЁ])/g, '$1 $2');
  return formatted;
};

/** Split step copy into its `\n`-separated paragraphs (see the W2 copy block). */
const splitParagraphs = (text?: string): string[] =>
  (text || '')
    .split('\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};

// Вспомогательная функция для очистки и закрытия compareStore
const clearAndCloseCompareStore = () => {
  const { clearSelectedPlans, close, setCompareViewCategory } =
    useCompareStore.getState();
  clearSelectedPlans();
  close();
  setCompareViewCategory(null);
};

const handlePrevClick = async (
  onPrev: ((e?: React.BaseSyntheticEvent) => void) | undefined,
  handlePreviousStep: () => void,
  e?: React.BaseSyntheticEvent
) => {
  clearAndCloseCompareStore();
  // W2 mode must skip the B2C tier/underwriting read, matching handleNextClick
  // (the store also no-ops via guardB2CSideEffect; this avoids the dead await).
  if (!useW2Mode.getState().active) {
    await useApplicationStore.getState().updateTierFromApplicationState();
  }
  if (onPrev) {
    onPrev(e);
  } else {
    handlePreviousStep();
  }
};

export const OnboardingFormStepLayout = ({
  children,
  handleFormSubmit,
  onPrev,
  onChange,
  isFormValid,
  idStep: propIdStep,
  title: propTitle,
  description: propDescription,
  subscription: propSubscription,
  hideNextButton,
  isSubmitText,
  onlyImage,
  headerClassName,
  isLoading: propIsLoading,
  backgroundClassName,
  isAnimationScreen,
  nextButtonLabel,
  headerAnimationKey,
  contentAnimationKey,
  disabled = false, // TODO надо будет проверить и уточнить
}: {
  children: React.ReactNode | React.ReactNode[];
  handleFormSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onPrev?: (e?: React.BaseSyntheticEvent) => void;
  onChange?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isFormValid?: boolean;
  idStep?: string;
  title?: string;
  description?: string;
  subscription?: string;
  hideNextButton?: boolean;
  isSubmitText?: boolean;
  onlyImage?: boolean;
  headerClassName?: string;
  isLoading?: boolean;
  backgroundClassName?: string;
  isAnimationScreen?: boolean;
  nextButtonLabel?: string;
  headerAnimationKey?: string;
  contentAnimationKey?: string;
  disabled?: boolean;
}) => {
  const {
    currentFormStep,
    formData,
    isLocalLoading,
    handleNextStep,
    handlePreviousStep,
    setIsLocalLoading,
  } = useFormControls();

  const childrenBlockRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const onScroll = useCallback(() => {
    const el = childrenBlockRef.current;
    if (el) setIsScrolled(el.scrollTop > 30);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentFormStep?.id]);

  useEffect(() => {
    const el = document.getElementById('childrenBlock');
    if (!el) return;
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [currentFormStep?.id, onScroll]);

  const headerVariants = usePageTransitionVariants();
  const contentVariants = usePageTransitionVariants(0.1);

  const formSteps = useFormSteps();
  const number = currentFormStep?.number;
  const idStep = propIdStep ?? currentFormStep?.id;
  const title = propTitle ?? currentFormStep?.title;
  const description = propDescription ?? currentFormStep?.description;
  const subscription = propSubscription ?? currentFormStep?.subscription;

  useEffect(() => {
    if (idStep) {
      useAIStore.getState().setCurrentPage(idStep);
    }
  }, [idStep]);

  const isLoading = propIsLoading || isLocalLoading;
  const [updateError, setUpdateError] = useState<string | null>(null);

  const {
    createApplication,
    getCurrentApplication,
    updateApplicationById,
    currentApplicationId,
    currentApplicationStatus,
    applications,
    activeSubscriptionPlans,
    signedAt,
    fetchActiveSubscriptionPlans,
    phqUnderReviewAt,
    acceptedTier,
    isLoading: isApplicationLoading,
  } = useApplicationStore();
  const { isUnderReview, sessionLockedForHumanReview, checkUnderReviewStatus } =
    useUnderReviewStore();
  const { isApplicationAccepted, setModalOpen: setAcceptanceWarningModalOpen } =
    useAcceptanceChangeStore();
  const { data } = useOnboardingStore.getState();

  const plans = useInsuranceStore((state) => state.plans);

  // Reactive read (store-access convention) — drives the W2-only copy-block
  // paragraph rule below. B2C is byte-identical when the flag is off.
  const isW2Copy = useIsW2Mode();

  // Reactive: derived from store subscription (acceptedTier above)
  const hasAcceptedTier = !!acceptedTier;

  // State to track if user has signed contract (AfterSignIn modal)
  const [isAfterSignInModalOpen, setIsAfterSignInModalOpen] =
    useState<boolean>(false);

  const isHealthOrDependentsSection =
    currentFormStep?.sectionName === FormStepSections.HealthQuestion ||
    currentFormStep?.sectionName === FormStepSections.Dependents;
  const allowMedicalOrDentalInteraction =
    !signedAt &&
    (currentFormStep?.sectionName === FormStepSections.Medical ||
      currentFormStep?.sectionName === FormStepSections.Dental) &&
    hasAcceptedTier &&
    !isAfterSignInModalOpen;
  const allowFinishInteraction =
    currentFormStep?.sectionName === FormStepSections.Finish &&
    !isAfterSignInModalOpen;
  const baseBlocked =
    disabled ||
    isUnderReview ||
    isApplicationAccepted ||
    isAfterSignInModalOpen ||
    (hasAcceptedTier && isHealthOrDependentsSection);
  const isBlockedTmp =
    baseBlocked &&
    !allowMedicalOrDentalInteraction &&
    !allowFinishInteraction &&
    idStep !== 'coverage_start_date';

  const isBlocked = isBlockedTmp || phqUnderReviewAt || sessionLockedForHumanReview;

  // Handler for blocked content click
  const handleBlockedContentClick = () => {
    const blockModal = phqUnderReviewAt;
    const isPlansPage =
      currentFormStep?.sectionName === 'Health Plans' ||
      currentFormStep?.sectionName === 'Extra Protection';

    if (sessionLockedForHumanReview) return;

    if (signedAt && isPlansPage && !blockModal) {
      const hideModal =
        typeof window !== 'undefined' &&
        localStorage.getItem(ACCEPTANCE_WARNING_LS_KEY) === '1';
      if (!hideModal) {
        setAcceptanceWarningModalOpen(true);
        return;
      }
    }

    // Open AcceptanceWarningModal if blocked and not under review
    if (isBlocked && !blockModal) {
      localStorage.removeItem(ACCEPTANCE_WARNING_LS_KEY);
      setAcceptanceWarningModalOpen(true);
    }
  };

  useEffect(() => {
    fetchActiveSubscriptionPlans();
  }, [fetchActiveSubscriptionPlans]);

  useEffect(() => {
    // getCurrentApplication();
  }, []);

  // Check under-review status on mount
  useEffect(() => {
    checkUnderReviewStatus();
  }, []);

  // Check if user has signed contract (show AfterSignIn modal)
  const appSignedAt =
    (
      applications?.[0] as
        | { signature?: { signedAt?: string | null } }
        | undefined
    )?.signature?.signedAt ?? null;
  useEffect(() => {
    const isPaymentPage =
      currentFormStep?.sectionName === 'Finish Up' ||
      currentFormStep?.id === 'Finish' ||
      (idStep?.startsWith('Finish') ?? false);
    if (isPaymentPage) {
      setIsAfterSignInModalOpen(false);
      return;
    }
    if (signedAt !== null && signedAt !== undefined) {
      setIsAfterSignInModalOpen(true);
      return;
    }
    setIsAfterSignInModalOpen(appSignedAt !== null && appSignedAt !== undefined);
  }, [
    signedAt,
    appSignedAt,
    currentFormStep?.sectionName,
    currentFormStep?.id,
    idStep,
  ]);

  useEffect(() => {
    if (applications.length === 0) return;

    const currentData = useOnboardingStore.getState().data;
    const hasUserFilledData =
      currentData?.applicants?.[0]?.firstName ||
      currentData?.applicants?.[0]?.lastName;
    if (JSON.stringify(currentData) !== '{}' || hasUserFilledData) return;

    const _data = transformApplicationToOnboardingData(applications[0], plans);
    if (Object.keys(_data).length === 0) return;

    useOnboardingStore.getState().setData(_data);
  }, [applications, plans]);

  if (!currentFormStep) return null;

  const MEDICATION_VALIDATION_ERROR = 'MEDICATION_VALIDATION_FAILED';
  const CONTACT_VALIDATION_ERROR = 'CONTACT_VALIDATION_FAILED';

  const handleNextClick = async (e?: React.BaseSyntheticEvent) => {
    // W2 mode reuses the branded B2C profile/PHQ screens but forks persistence to
    // the W2 endpoint (ADR-0003/0011): it must NOT touch the paying-customer
    // `applications` tables or their tier/underwriting reads. Advance the step but
    // skip every B2C application side effect here — the final PHQ submit forks to
    // `employees.phqPayload` (see HealthQuestionForm).
    const isW2 = useW2Mode.getState().active;
    clearAndCloseCompareStore();
    if (!isW2) {
      await useApplicationStore.getState().updateTierFromApplicationState();
    }
    if (handleFormSubmit) {
      try {
        await handleFormSubmit(e);
      } catch (err) {
        const code = (err as Error & { code?: string })?.code;
        if (
          code === MEDICATION_VALIDATION_ERROR ||
          code === CONTACT_VALIDATION_ERROR ||
          code === CONDITION_DESCRIPTION_VALIDATION_ERROR
        ) {
          return;
        }
        throw err;
      }
    } else {
      const data = useOnboardingStore.getState().data;
      useOnboardingStore.getState().setData(data);
    }
    const updatedFormData = useOnboardingStore.getState().data;

    if (signedAt) {
      console.warn('Application is signed. Update skipped.');
      return;
    }

    const { phqUnderReviewAt: underReview } = useApplicationStore.getState();
    if (underReview) {
      console.warn('Application is under review');
      return;
    }

    if (isFormValid && !isW2) {
      try {
        setIsLocalLoading(true);

        const applicationData = transformApplicationData(
          updatedFormData,
          plans
        );

        let existingApplicationId =
          currentApplicationStatus === 1
            ? null
            : currentApplicationId ||
              (applications.length > 0 && applications[0]?.id
                ? applications[0].id
                : null);

        if (!existingApplicationId) {
          try {
            await getCurrentApplication();
            const updatedState = useApplicationStore.getState();
            existingApplicationId =
              updatedState.currentApplicationStatus === 1
                ? null
                : updatedState.currentApplicationId ||
                  (updatedState.applications.length > 0 &&
                  updatedState.applications[0]?.id
                    ? updatedState.applications[0].id
                    : null);
          } catch (error) {
            console.log('no existing application found');
          }
        }

        if (existingApplicationId) {
          await updateApplicationById(existingApplicationId, applicationData);
        } else {
          await createApplication(applicationData);
        }
      } catch (err) {
        console.error('[handleNextClick] Update failed:', err);
        const axiosErr = err as { response?: { data?: unknown; status?: number } };
        if (axiosErr?.response?.data) {
          console.error('[handleNextClick] Backend response:', axiosErr.response.data);
        }
        setUpdateError(getUpdateErrorMessage(err));
        // Stop flow on error - don't proceed to next step
        return;
      } finally {
        setIsLocalLoading(false);
      }
    }
  };

  const Header = (
    <motion.header
      key={`header_${headerAnimationKey || currentFormStep.id}`}
      variants={headerVariants}
      initial="initial"
      animate="animate"
      // exit="exit"
      className={cn(
        'w-full relative rounded-3xl p-5 lg:pt-10 lg:pb-8 lg:px-7.5 bg-primary max-lg:min-w-fit flex flex-col gap-4 max-xl:min-h-102.5',
        // W2 omits `h-full`: the step wrapper grows with its content, so a
        // percentage height resolves to `auto` here — and because it is set
        // EXPLICITLY, flex `align-items: stretch` is suppressed and the panel
        // collapses to its content while the image column fills the row. With
        // the cross size left auto, stretch makes both columns equal height.
        isW2Copy || 'h-full',
        'lg:justify-between max-lg:justify-start max-lg:h-auto max-lg:min-h-0 max-s:min-h-0 text-white 2xl:max-w-[34.83%]',
        onlyImage && 'max-lg:min-h-0',
        headerClassName
        // onlyImage && 'max-lg:mb-6'
      )}
    >
      <div className="bg-primary absolute top-25 -right-1 w-8 h-8 pointer-events-none select-none max-2xl:hidden rotate-45" />
      <section
        id="add-header-left-text"
        className="flex flex-col gap-6 relative overflow-y-auto max-md:gap-2 h-full max-md:h-auto lg:pr-1.5"
      >
        <p className="header-text text-[19px] sticky top-0 bg-primary leading-6 tracking-[30%] uppercase align-middle max-md:text-[14px]">
          step {number} /{' '}
          {isW2Copy
            ? W2_PHQ_STEP_LABELS.length
            : formSteps.filter((s) => !s.isIntermediateStep).length}
        </p>
        <h2 className="font-bold text-2xl leading-7 lg:leading-10 max-md:tracking-[1px] lg:text-4xl 3xl:text-[40px]">
          {title}
        </h2>
        {isW2Copy ? (
          /*
           * W2 copy block: each `\n`-separated paragraph of the step copy is its
           * own <p>, and the BLOCK carries the spacing rule (1em top margin on a
           * 1.6 line-height) rather than any one element carrying a spacer — the
           * shared `formatText` <br /> alone left paragraphs jammed together
           * (client ticket T3). W2-gated so the B2C copy render is untouched.
           */
          <div className="text-base leading-[1.6] max-md:mt-2.5 [&>p+p]:mt-[1em]">
            {splitParagraphs(description).map((paragraph, i) => (
              <p
                key={i}
                dangerouslySetInnerHTML={{ __html: formatText(paragraph) }}
              />
            ))}
          </div>
        ) : (
          <p
            className="text-base max-md:leading-normal max-md:mt-2.5 max-lg:text-base"
            dangerouslySetInnerHTML={{
              __html: formatText(description || ''),
            }}
          />
        )}
        {number === 3 && subscription && (
          <p
            className="text-base max-md:leading-normal max-md:mt-2.5 max-lg:text-base"
            dangerouslySetInnerHTML={{
              __html: formatText(subscription),
            }}
          />
        )}
      </section>
      {number === 1 ? (
        <div id="app-header-left-nav" className="max-2xl:hidden mt-auto flex">
          <button
            data-testid="onboarding-next"
            className={cn(
              'border border-white whitespace-nowrap relative mt-2 bg-white items-center font-bold uppercase w-full flex justify-center rounded-full px-15 py-1.5 text-[19px] cursor-pointer text-primary md:py-3.5',
              isApplicationLoading && 'opacity-50 pointer-events-none'
            )}
            disabled={isApplicationLoading}
            onClick={() => handleNextStep(formData)}
          >
            <span>Let's Get Started!</span>
            <RightArrowIcon
              color="var(--color-primary)"
              className="absolute right-5"
            />
          </button>
        </div>
      ) : (
        <div id="app-header-left-nav" className="max-2xl:hidden flex">
          <NavigationButtons
            handleFormSubmit={handleNextClick}
            onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
            onChange={onChange}
            isFormValid={isFormValid}
            hideNextButton={hideNextButton}
            isLoading={isLoading}
            isSubmitText={isSubmitText}
            nextButtonLabel={nextButtonLabel}
          />
        </div>
      )}
    </motion.header>
  );

  return (
    <StepBlockContext.Provider value={isBlocked}>
      <div
        data-testid="onboarding-step"
        data-step-id={idStep}
        data-section={currentFormStep?.sectionName}
        className={cn(
          'flex flex-col justify-center relative pb-0 max-xl:pb-0',
          // W2 differs only in the 24px panel↔image gap; the grow-when-tall
          // behaviour comes from `#step-parent` being a flex item of the shell's
          // `min-h-full` section (see OnboardingForm).
          // W2 drops B2C's `pr-1` scrollbar gutter so the right inset matches the
          // 24px used on every other edge of the shell.
          // `2xl:min-h-0` drops the flex `min-height: auto` floor at 2xl ONLY, so
          // a tall form can no longer grow the row past the viewport (which
          // pushed the maroon panel's nav buttons off the bottom edge) — the
          // content card's own `2xl:h-full overflow-auto` scroller takes the
          // overflow instead. Below 2xl the floor stays and the row grows into
          // the shell's <main> scroller, which is what keeps every line of copy
          // reachable in the narrow band.
          isW2Copy ? 'flex-1 gap-6 2xl:min-h-0' : 'h-full gap-4 pr-1',
          onlyImage ? '2xl:flex-row-reverse' : '2xl:flex-row'
        )}
      >
        {/* <StepNavigator
        onNext={handleNextClick}
        onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
        isFormValid={isFormValid}
      /> */}
        {onlyImage ? (
          <>
            <motion.div
              id="childrenBlock"
              key={
                headerAnimationKey || contentAnimationKey || currentFormStep.id
              }
              variants={contentVariants}
              initial="initial"
              animate="animate"
              // exit="exit"
              className={cn(
                'bg-card w-full rounded-2xl',
                // W2: take the welcome image OUT of flow so it stops driving the
                // step's height. In flow its natural height (taller than the
                // viewport) forces the whole fixed-viewport shell to scroll and
                // leaves the maroon panel short; out of flow the row's height
                // comes from the shell, the image stretches to it and
                // `object-cover` crops. B2C is untouched.
                isW2Copy &&
                  'relative overflow-hidden [&>img]:absolute [&>img]:inset-0'
              )}
            >
              {children}
            </motion.div>
            <AnimatePresence mode="wait">{Header}</AnimatePresence>
          </>
        ) : (
          <>
            <AnimatePresence mode="wait">{Header}</AnimatePresence>
            <motion.div
              key={
                headerAnimationKey || contentAnimationKey || currentFormStep.id
              }
              variants={contentVariants}
              initial="initial"
              animate="animate"
              // exit="exit"
              className={cn(
                'w-full xl:pr-4 xl:pt-2.5 xl:pb-6 box-border rounded-2xl relative',
                // Same reason as the maroon panel above: in W2 the explicit
                // `h-full` would resolve to `auto` and suppress stretch, leaving
                // the white card short next to a full-height panel.
                isW2Copy || 'h-full',
                backgroundClassName ?? 'bg-card'
              )}
            >
              <div
                ref={childrenBlockRef}
                id="childrenBlock"
                onScroll={onScroll}
                className={cn(
                  'scrollbar-gutter-stable',
                  'w-full flex flex-col pt-5 xl:pt-2.5 px-5 md:px-7.5 xl:pr-3.5 pb-8 xl:pb-2 overflow-auto 2xl:max-h-full 2xl:h-full max-2xl:relative',
                  isAnimationScreen
                    ? 'items-center justify-center'
                    : 'justify-between',
                  (title === 'Time to talk about medications.' ||
                    title === 'Time to talk about medications' ||
                    title === 'First up: some quick health questions' ||
                    title === "Let's answer some quick health questions") &&
                    'bg-transparent pb-0 max-md:px-3 md:bg-card rounded-2xl'
                )}
                onClick={handleBlockedContentClick}
              >
                <div
                  className={cn(
                    'h-full',
                    isBlocked ? 'pointer-events-none' : ''
                  )}
                >
                  {children}
                </div>
              </div>
              {isScrolled && (
                <div className="pointer-events-none absolute top-0 left-0 right-0 h-20 bg-linear-to-b from-white via-white/60 to-transparent z-10 rounded-t-2xl hidden 2xl:block" />
              )}
              {/* FADE EFFECT */}
              {/* <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-28 bg-linear-to-t from-white via-white/80 to-transparent z-10 rounded-b-2xl hidden 2xl:block" /> */}
            </motion.div>
          </>
        )}
        <BottomNavigationButtons
          handleFormSubmit={handleNextClick}
          onPrev={(e) => handlePrevClick(onPrev, handlePreviousStep, e)}
          onChange={onChange}
          isFormValid={isFormValid}
          isLoading={isLoading}
          isSubmitText={isSubmitText}
          hideNextButton={hideNextButton}
          nextButtonLabel={nextButtonLabel}
        />
      </div>
      <UpdateErrorModal
        isOpen={!!updateError}
        message={updateError || ''}
        onClose={() => setUpdateError(null)}
      />
    </StepBlockContext.Provider>
  );
};
